import type { ApiResult } from '../types';
import { i18n } from '../i18n';
import { formatRelativeTimeAgo } from '../utils/dateFormat';
import { parseNotificationMessage } from '../utils/notificationMessage';
import {
  buildLegalOutcomeNotificationPath,
  isLegalOutcomeNotification,
  isNotificationUuid,
  isTaskRelevantNotification,
  resolveNotificationUserTaskId,
  resolveTaskNotificationPath,
} from '../utils/notificationNavigation';
import { fetchExtractedCaseOutcomeByCaseRequest } from './legalKnowledgeArchive';
import { getCaseRequestWorkflowProgress } from './caseRequestDetails';
import { caseApiRequest } from './apiClient';

export type NotificationVisualType = 'comm' | 'esc' | 'remind' | 'status' | 'success' | 'info';

export type SystemNotification = {
  id: string;
  type: NotificationVisualType;
  title: string;
  body: string;
  createdAt: string;
  createdAtLabel: string;
  caseRef?: string;
  read: boolean;
  /** Task/Case Details or Legal Knowledge Archive deep link. */
  actionUrl?: string;
  taskNavigable: boolean;
  notificationTypeCode?: string;
  scenario?: string;
  caseRequestId?: string;
  workflowTaskId?: string;
  refTableName?: string;
  refTableId?: string;
  extractedCaseOutcomeId?: string;
  actionLabel?: string;
  actionPrimary?: boolean;
};

export type NotificationListPage = {
  items: SystemNotification[];
  totalElements: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  unreadCount?: number;
};

type ApiNotificationRow = Record<string, unknown>;

type ApiNotificationPage = {
  content?: ApiNotificationRow[];
  notifications?: ApiNotificationRow[];
  items?: ApiNotificationRow[];
  totalElements?: number;
  totalCount?: number;
  total_count?: number;
  totalPages?: number;
  number?: number;
  pageNumber?: number;
  size?: number;
  pageSize?: number;
  unreadCount?: number;
  unread_count?: number;
  totalUnread?: number;
  total_unread?: number;
};

function strField(row: ApiNotificationRow, ...keys: string[]): string {
  for (const key of keys) {
    const v = row[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

function boolField(row: ApiNotificationRow, ...keys: string[]): boolean | undefined {
  for (const key of keys) {
    const v = row[key];
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      if (s === 'true' || s === 'read' || s === 'yes') return true;
      if (s === 'false' || s === 'unread' || s === 'no') return false;
    }
  }
  return undefined;
}

function nestedTypeName(row: ApiNotificationRow): string {
  for (const key of ['notificationType', 'notification_type', 'type']) {
    const v = row[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const o = v as Record<string, unknown>;
      const name = o.name ?? o.code ?? o.typeCode ?? o.type_code;
      if (typeof name === 'string' && name.trim()) return name.trim();
    }
  }
  return '';
}

export function resolveNotificationVisualType(raw: string, title = '', body = ''): NotificationVisualType {
  const hay = `${raw} ${title} ${body}`.toUpperCase();
  if (/(ESCAL|SLA|BREACH|WARNING)/.test(hay)) return 'esc';
  if (/(REMIND|PENDING|VERIF|DEADLINE)/.test(hay)) return 'remind';
  if (/(STATUS\s*CHANGE|STATUS_CHANGE|STATUS)/.test(hay)) return 'status';
  if (/(TASK_SUBMITTED|SUBMIT|SUCCESS|CONFIRM|ASSIGNMENT|ASSIGN)/.test(hay)) return 'success';
  if (/(UPLOAD|DOCUMENT|INFO\s*REQUIRED|ADDITIONAL\s*INFO)/.test(hay)) return 'info';
  if (/(COMM|MESSAGE|ADMIN)/.test(hay)) return 'comm';
  return 'comm';
}

function isPrimaryActionLabel(label: string, type: NotificationVisualType): boolean {
  const u = label.toUpperCase();
  if (u.includes('UPLOAD')) return true;
  return type === 'info' && u.includes('DOCUMENT');
}

function adaptNotificationRow(row: ApiNotificationRow): SystemNotification | null {
  const id = strField(row, 'id', 'notificationId', 'notification_id');
  if (!id) return null;

  const title = strField(row, 'title', 'notificationTitle', 'notification_title', 'subject');
  const rawBody = strField(
    row,
    'body',
    'message',
    'notificationMessage',
    'notification_message',
    'description',
    'content',
  );
  const { text: body, caseRef: bodyCaseRef } = parseNotificationMessage(rawBody);
  const createdAt = strField(
    row,
    'createdAt',
    'created_at',
    'createdDateTime',
    'created_date_time',
    'sentAt',
    'sent_at',
  );
  const caseRef = strField(
    row,
    'caseRef',
    'case_ref',
    'caseReference',
    'case_reference',
    'referenceNo',
    'reference_no',
    'fileReferenceNumber',
    'file_reference_number',
    'applicationRef',
    'application_ref',
  );
  const actionUrlFromApi = strField(row, 'actionUrl', 'action_url', 'redirectUrl', 'redirect_url', 'link', 'deepLink', 'deep_link');
  const caseRequestId = strField(row, 'caseRequestId', 'case_request_id');
  const workflowTaskId = strField(
    row,
    'workflowTaskId',
    'workflow_task_id',
    'workflowTaskInstanceId',
    'workflow_task_instance_id',
  );
  const notificationTypeCode = strField(row, 'notificationTypeCode', 'notification_type_code');
  const scenario = strField(row, 'scenario');
  const refTableName = strField(row, 'refTableName', 'ref_table_name');
  const refTableId = strField(row, 'refTableId', 'ref_table_id');
  const extractedCaseOutcomeId = strField(
    row,
    'extractedCaseOutcomeId',
    'extracted_case_outcome_id',
  );
  const isLegalOutcome = isLegalOutcomeNotification({ notificationTypeCode, scenario });
  const taskContext = {
    caseRequestId,
    workflowTaskId,
    refTableName,
    refTableId,
    notificationTypeCode,
    scenario,
  };
  const legalOutcomePath = isLegalOutcome
    ? buildLegalOutcomeNotificationPath({
        extractedCaseOutcomeId,
        caseRequestId,
        status: 'DRAFT',
      })
    : undefined;
  const taskNavigableByTask = isTaskRelevantNotification(taskContext);
  const taskCasePath = resolveTaskNotificationPath({
    actionUrlFromApi,
    ...taskContext,
  });
  const actionUrl = legalOutcomePath || taskCasePath || actionUrlFromApi || undefined;
  const navigable = Boolean(
    actionUrl ||
      taskNavigableByTask ||
      (isLegalOutcome && (isNotificationUuid(extractedCaseOutcomeId) || isNotificationUuid(caseRequestId))),
  );
  const actionLabel = strField(
    row,
    'actionLabel',
    'action_label',
    'buttonLabel',
    'button_label',
    'actionButtonText',
    'action_button_text',
  );

  const readFlag = boolField(row, 'read', 'isRead', 'is_read');
  const readStatus = strField(row, 'readStatus', 'read_status').toLowerCase();
  const read =
    readFlag ??
    (readStatus ? readStatus === 'read' || readStatus === 'true' : false);

  const typeRaw = nestedTypeName(row) || notificationTypeCode || scenario;
  const type = resolveNotificationVisualType(typeRaw, title, body);

  return {
    id,
    type,
    title: title || i18n.t('notifications.defaultTitle'),
    body,
    createdAt,
    createdAtLabel: createdAt ? formatRelativeTimeAgo(createdAt) : '',
    caseRef: caseRef || bodyCaseRef || undefined,
    read,
    actionUrl,
    taskNavigable: navigable,
    notificationTypeCode: notificationTypeCode || undefined,
    scenario: scenario || undefined,
    caseRequestId: caseRequestId || undefined,
    workflowTaskId: workflowTaskId || undefined,
    refTableName: refTableName || undefined,
    refTableId: refTableId || undefined,
    extractedCaseOutcomeId: extractedCaseOutcomeId || undefined,
    actionLabel: actionLabel || undefined,
    actionPrimary: actionLabel ? isPrimaryActionLabel(actionLabel, type) : type === 'info',
  };
}

function unwrapNotificationPage(raw: unknown): ApiNotificationPage {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;

  if (Array.isArray(raw)) {
    return { content: raw as ApiNotificationRow[] };
  }

  if (Array.isArray(r.content)) return r as ApiNotificationPage;
  if (Array.isArray(r.notifications)) return { ...r, content: r.notifications as ApiNotificationRow[] };
  if (Array.isArray(r.items)) return { ...r, content: r.items as ApiNotificationRow[] };

  const data = r.data;
  if (data && typeof data === 'object') {
    return unwrapNotificationPage(data);
  }

  return r as ApiNotificationPage;
}

function normalizePageNumber(value: number | undefined, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) return value;
  return fallback;
}

export async function fetchNotifications(params: {
  /** 0-based page index (first page = 0). */
  pageNumber: number;
  pageSize: number;
  unreadOnly?: boolean;
  signal?: AbortSignal;
}): Promise<ApiResult<NotificationListPage>> {
  const pageNumber = Math.max(0, params.pageNumber);
  const pageSize = Math.max(1, params.pageSize);

  const qs = new URLSearchParams({
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
  });
  if (params.unreadOnly) {
    qs.set('isRead', 'false');
  }

  const res = await caseApiRequest<ApiNotificationPage | ApiNotificationRow[]>(
    `/v1/notifications?${qs.toString()}`,
    { signal: params.signal },
  );
  if (!res.ok) return { success: false, message: res.message };

  const page = unwrapNotificationPage(res.data);
  const content = Array.isArray(page.content) ? page.content : [];
  const items = content.map(adaptNotificationRow).filter((n): n is SystemNotification => n != null);

  const totalElements =
    typeof page.totalCount === 'number'
      ? page.totalCount
      : typeof page.total_count === 'number'
        ? page.total_count
        : typeof page.totalElements === 'number'
          ? page.totalElements
          : items.length;
  const computedPages = Math.max(1, Math.ceil(totalElements / pageSize) || 1);
  const totalPages =
    typeof page.totalPages === 'number' && page.totalPages > 0
      ? page.totalPages
      : totalElements === 0
        ? 1
        : computedPages;

  const unreadCount =
    typeof page.unreadCount === 'number'
      ? page.unreadCount
      : typeof page.unread_count === 'number'
        ? page.unread_count
        : typeof page.totalUnread === 'number'
          ? page.totalUnread
          : typeof page.total_unread === 'number'
            ? page.total_unread
            : undefined;

  return {
    success: true,
    data: {
      items,
      totalElements,
      totalPages,
      pageNumber: normalizePageNumber(page.pageNumber ?? page.number, pageNumber),
      pageSize: typeof page.pageSize === 'number' ? page.pageSize : typeof page.size === 'number' ? page.size : pageSize,
      unreadCount,
    },
  };
}

export type NotificationCounts = {
  unreadCount: number;
  totalCount: number;
};

/** Lightweight fetch for header badge — uses counts from list API (`data.unreadCount`, `data.totalCount`). */
export async function fetchNotificationCounts(
  signal?: AbortSignal,
): Promise<ApiResult<NotificationCounts>> {
  const res = await fetchNotifications({ pageNumber: 0, pageSize: 5, signal });
  if (!res.success) return { success: false, message: res.message };
  return {
    success: true,
    data: {
      unreadCount: res.data.unreadCount ?? 0,
      totalCount: res.data.totalElements,
    },
  };
}

export async function markNotificationRead(notificationId: string): Promise<ApiResult<void>> {
  if (!notificationId.trim()) return { success: false, message: 'Notification id is missing.' };

  const res = await caseApiRequest<unknown>(
    `/v1/notifications/${encodeURIComponent(notificationId)}/mark-read`,
    { method: 'PUT' },
  );
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

export async function markAllNotificationsRead(): Promise<ApiResult<void>> {
  const res = await caseApiRequest<unknown>('/v1/notifications/mark-all-read', { method: 'PUT' });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

/** Resolve navigation target for notifications that need a follow-up API lookup. */
export async function resolveNotificationActionUrl(
  notification: SystemNotification,
): Promise<string | undefined> {
  const taskContext = {
    caseRequestId: notification.caseRequestId,
    workflowTaskId: notification.workflowTaskId,
    refTableName: notification.refTableName,
    refTableId: notification.refTableId,
  };

  if (
    isLegalOutcomeNotification({
      notificationTypeCode: notification.notificationTypeCode,
      scenario: notification.scenario,
    })
  ) {
    if (notification.actionUrl) return notification.actionUrl;

    const fromPayload = buildLegalOutcomeNotificationPath({
      extractedCaseOutcomeId: notification.extractedCaseOutcomeId,
      caseRequestId: notification.caseRequestId,
      status: 'DRAFT',
    });
    if (fromPayload) return fromPayload;

    const caseRequestId = (notification.caseRequestId ?? '').trim();
    if (!isNotificationUuid(caseRequestId)) return undefined;

    try {
      const detail = await fetchExtractedCaseOutcomeByCaseRequest(caseRequestId, 'DRAFT');
      const outcomeId = detail.id.trim();
      return buildLegalOutcomeNotificationPath({
        extractedCaseOutcomeId: outcomeId,
        caseRequestId,
        status: 'DRAFT',
      });
    } catch {
      return undefined;
    }
  }

  const directPath = resolveTaskNotificationPath({
    actionUrlFromApi: notification.actionUrl,
    ...taskContext,
  });
  if (directPath && resolveNotificationUserTaskId(taskContext)) return directPath;
  if (directPath) return directPath;

  // SLA / task notifications may omit caseRequestId — resolve it from workflow-progress.
  const userTaskId = resolveNotificationUserTaskId(taskContext);
  if (!userTaskId) return notification.actionUrl;

  const progress = await getCaseRequestWorkflowProgress(userTaskId);
  if (!progress.success) return notification.actionUrl;

  const resolvedCaseRequestId =
    (typeof progress.data.case_request_id === 'string' ? progress.data.case_request_id.trim() : '') ||
    (typeof progress.data.caseRequestId === 'string' ? progress.data.caseRequestId.trim() : '');
  if (!isNotificationUuid(resolvedCaseRequestId)) return notification.actionUrl;

  return resolveTaskNotificationPath({
    caseRequestId: resolvedCaseRequestId,
    workflowTaskId: userTaskId,
  });
}
